import { useQuery } from '@tanstack/react-query';
import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';
import type { LoginHistoryEntry, LoginHistoryResult } from '@xanectech/types';
import { error } from 'console';

const logger = createLogger('Data - useLoginHistory');

export const useLoginHistory = (
  page = 1,
  pageSize = 50,
  userIdFilter?: string,
): ReturnType<typeof useQuery<LoginHistoryResult>> => {
  return useQuery<LoginHistoryResult>({
    queryKey: ['login-history', page, pageSize, userIdFilter],
    queryFn: async (): Promise<LoginHistoryResult> => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from('login_history')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);

      if (userIdFilter) {
        query = query.eq('user_id', userIdFilter);
        // Log access to sensitive data when admin views specific user's data
        await supabase.rpc('log_login_history_access', {
          accessed_user_id: userIdFilter,
        });
      }

      const { data, error, count } = await query;

      if (error) throw error;
      const entries = (data ?? []) as LoginHistoryEntry[];
      const total = typeof count === 'number' ? count : entries.length;
      logger.info(
        `Fetched login history entries: ${entries.length} of ${total}`,
      );

      return { entries, count: total };
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev as LoginHistoryResult | undefined,
  });
};

export const logUserLogin = async (
  userId: string,
  userEmail: string,
  ipAddress?: string,
  userAgent?: string,
  portal?: string,
) => {
  try {
    const { error } = await supabase.rpc('log_user_login', {
      p_user_id: userId,
      p_user_email: userEmail,
      p_ip_address: ipAddress ?? undefined,
      p_user_agent: userAgent ?? undefined,
      p_login_method: 'password',
      p_portal: portal ?? 'trueid-locker',
    });

    if (error) {
      logger.error('Error logging user login:', { error });
    }
  } catch (err) {
    logger.error('Failed to log user login:', {
      error: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
    });
  }
};

export const checkRateLimit = async (
  identifier: string,
  maxAttempts: number = 5,
  windowMinutes: number = 15,
  actionType: string = 'login',
): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('enhanced_rate_limit_check', {
      p_identifier: identifier,
      p_max_attempts: maxAttempts,
      p_window_minutes: windowMinutes,
      p_action_type: actionType,
    });

    if (error) {
      logger.error('Error checking rate limit:', { error });
      return false; // Fail closed for security
    }

    return data || false;
  } catch (err) {
    logger.error('Failed to check rate limit:', {
      error: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
    });
    return false; // Fail closed for security
  }
};
